package songwritersnotebook.app.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import songwritersnotebook.app.data.GuitarTab

import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.Stop
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton

@Composable
fun TabDisplay(
    tab: GuitarTab,
    modifier: Modifier = Modifier,
    globalFootnotes: Map<String, Int> = emptyMap(),
    isPlaying: Boolean = false,
    onPlay: (() -> Unit)? = null,
    remotePresences: List<songwritersnotebook.app.data.UserPresence> = emptyList()
) {
    // 1. Identify instructions in THIS tab that are too long
    val localLongInstructions = remember(tab) {
        val list = mutableListOf<String>()
        tab.columns.forEach { col ->
            val noteLen = col.notes.maxOfOrNull { if (it == "-" || it.isBlank()) 1 else it.length } ?: 1
            if (col.instruction.length > noteLen) {
                if (!list.contains(col.instruction)) {
                    list.add(col.instruction)
                }
            }
        }
        list
    }

    // 2. Resolve indices (Prefer Global, fallback to Local)
    val footnoteIndexMap = remember(localLongInstructions, globalFootnotes) {
        if (globalFootnotes.isNotEmpty()) {
            localLongInstructions.associateWith { text ->
                globalFootnotes[text] ?: (globalFootnotes.values.maxOrNull() ?: 0) + localLongInstructions.indexOf(text) + 1
            }
        } else {
            localLongInstructions.mapIndexed { index, text -> text to (index + 1) }.toMap()
        }
    }

    Column(
        modifier = modifier
            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f), RoundedCornerShape(4.dp))
            .border(1.dp, MaterialTheme.colorScheme.outlineVariant, RoundedCornerShape(4.dp))
            .padding(8.dp)
    ) {
        // 1. Tab Header (Name, Presence, and Play Button)
        val displayName = tab.name.ifBlank { "Untitled Tab" }
        val hasPresence = remotePresences.isNotEmpty()
        val hasPlay = onPlay != null

        Row(
            modifier = Modifier.padding(bottom = 4.dp), // no fillMaxWidth, allows wrap
            horizontalArrangement = Arrangement.spacedBy(8.dp), // space text/presences and play button
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Left part: Name and Presence
            Row(
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = displayName,
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    // widthIn ensures extremely long titles don't stretch indefinitely
                    modifier = Modifier.widthIn(max = 240.dp)
                )
                
                if (hasPresence) {
                    Spacer(Modifier.width(8.dp))
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(4.dp)
                    ) {
                        remotePresences.forEach { presence ->
                            Box(
                                modifier = Modifier
                                    .size(8.dp)
                                    .background(androidx.compose.ui.graphics.Color(if (presence.color != 0) presence.color else songwritersnotebook.app.data.UserPresence.generateUserColor(presence.userId)), androidx.compose.foundation.shape.CircleShape)
                                    .border(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.5f), androidx.compose.foundation.shape.CircleShape)
                            )
                        }
                    }
                }
            }
            
            // Right part: Play Button
            if (hasPlay) {
                IconButton(onClick = onPlay!!, modifier = Modifier.size(24.dp)) {
                    Icon(
                        imageVector = if (isPlaying) Icons.Default.Stop else Icons.Default.PlayArrow,
                        contentDescription = if (isPlaying) "Stop Tab Audio" else "Play Tab Audio",
                        tint = MaterialTheme.colorScheme.primary
                    )
                }
            }
        }

        // 2. The Grid
        Row(
            modifier = Modifier.height(IntrinsicSize.Min) // Important for Bar Lines to fill height
        ) {
            // Fixed Header Column (Inst + Strings)
            Column(
                modifier = Modifier.padding(end = 8.dp),
                verticalArrangement = Arrangement.spacedBy(2.dp)
            ) {
                // Instruction Header
                Text(
                    text = "Inst",
                    style = TextStyle(
                        fontFamily = FontFamily.Monospace,
                        fontSize = 10.sp,
                        color = MaterialTheme.colorScheme.secondary
                    ),
                    modifier = Modifier.height(14.dp) // Fixed height for alignment
                )

                // String Headers
                val strings = listOf("e", "B", "G", "D", "A", "E")
                strings.forEach { stringName ->
                    Text(
                        text = "$stringName|",
                        style = TextStyle(
                            fontFamily = FontFamily.Monospace,
                            fontSize = 12.sp,
                            color = MaterialTheme.colorScheme.primary
                        ),
                        modifier = Modifier.height(14.dp)
                    )
                }
            }

            // Dynamic Columns
            tab.columns.forEachIndexed { index, tabColumn ->
                // Bar Line Logic
                if (index > 0 && index % tab.subdivisions == 0) {
                    Box(
                        modifier = Modifier
                            .width(1.dp)
                            .fillMaxHeight()
                            .background(MaterialTheme.colorScheme.outline.copy(alpha = 0.5f))
                    )
                    Spacer(modifier = Modifier.width(2.dp))
                }

                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(2.dp)
                ) {
                    // Instruction Row
                    val noteLen = tabColumn.notes.maxOfOrNull { if (it == "-" || it.isBlank()) 1 else it.length } ?: 1
                    val rawInst = tabColumn.instruction
                    
                    // Determine display text
                    val displayText = if (rawInst.length > noteLen) {
                        val fnIndex = footnoteIndexMap[rawInst]
                        if (fnIndex != null) "(${fnIndex})" else rawInst
                    } else {
                        rawInst
                    }

                    Text(
                        text = displayText,
                        style = TextStyle(
                            fontFamily = FontFamily.Monospace,
                            fontSize = 10.sp,
                            fontWeight = FontWeight.Bold,
                            color = if (displayText.startsWith("(")) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.secondary
                        ),
                        modifier = Modifier
                            .height(14.dp)
                            .padding(horizontal = 2.dp)
                    )

                    // Notes Rows
                    tabColumn.notes.forEach { note ->
                        val displayChar = if (note.isBlank()) "-" else note
                        Text(
                            text = displayChar,
                            style = TextStyle(
                                fontFamily = FontFamily.Monospace,
                                fontSize = 12.sp,
                                color = MaterialTheme.colorScheme.onSurface
                            ),
                            modifier = Modifier
                                .height(14.dp)
                                .padding(horizontal = 2.dp)
                        )
                    }
                }
            }
        }

        // 3. Legend (Filtered for THIS tab, sorted by index)
        if (localLongInstructions.isNotEmpty()) {
            Spacer(modifier = Modifier.height(8.dp))
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
            Spacer(modifier = Modifier.height(4.dp))
            
            Text(
                text = "Legend:",
                style = MaterialTheme.typography.labelSmall,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            
            // Sort by index for nice display (e.g. (1) then (5) if (2-4) are elsewhere)
            val sortedInstructions = localLongInstructions.sortedBy { footnoteIndexMap[it] ?: Int.MAX_VALUE }
            
            sortedInstructions.forEach { text ->
                val index = footnoteIndexMap[text]
                if (index != null) {
                    Text(
                        text = "(${index}) $text",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.padding(start = 8.dp)
                    )
                }
            }
        }
    }
}
